import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Accordion, Icon, Modal } from "semantic-ui-react";
import moment from "moment";
import VoiceRecorderDialog from "./VoiceRecorderDialog";
import UploadAudioDialog from "./UploadAudioDialog";
import ViewTranscript from "./ViewTranscript";
import TranscriptsService from "../../../../../services/api/transcripts.service";
import Table2 from "../../../../templates/components/Table2";
import ModalBox from "../../../../templates/components/ModalBox";   


const TranscriptsPanel = ({
  appointmentId,
  patientId,
  onJobStarted,

  open,                 // when defined => controlled
  onToggle,             // parent callback when header is clicked


}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [showRecord, setShowRecord] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [viewing, setViewing] = useState(null);

  const isControlled = typeof open !== "undefined";
  const [internalOpen, setInternalOpen] = useState(false);
  const shownOpen = isControlled ? !!open : internalOpen;

  const handleTitleClick = (e) => {
    // let parent collapse others, etc.
    if (onToggle) onToggle(e);
    if (!isControlled) setInternalOpen((v) => !v);
  };



  const fetchTranscripts = async () => {
    if (!appointmentId && !patientId) return;
    setLoading(true);
    try {
      const req = { };
      if (appointmentId) {
        req.appointmentId = appointmentId;
      }
      const res = await TranscriptsService.getTranscripts(req, String(patientId));
      const data = Array.isArray(res?.data) ? res.data.slice() : [];
      data.sort((a, b) => {
        const ta = a?.dateCreated ? Date.parse(a.dateCreated) : 0;
        const tb = b?.dateCreated ? Date.parse(b.dateCreated) : 0;
        return tb - ta; // newest first
      });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranscripts();
  }, [appointmentId, patientId]);

  const columns = useMemo(
    () => [
      {
        id: "dateCreated",
        Header: "Date",
        accessor: (cell) =>
          cell?.dateCreated
            ? moment.parseZone(cell.dateCreated).local().format("ddd MM/DD/YYYY hh:mma")
            : "",
      },
      {
        id: "medicalScribeJobStatus",
        Header: "Status",
        accessor: (cell) => (cell?.medicalScribeJobStatus || "").toString(),
      },
      {
        id: "actions",
        Header: "Actions",
        textAlign: "center",
        disableSortBy: true,
        accessor: (t) => (
          <div className="d-flex justify-content-center">
            {(t?.medicalScribeJobStatus || "").toUpperCase() === "COMPLETED" && (
              <div className="btn-group">
                <button
                  className="btn btn-primary"
                  title="View Transcript"
                  onClick={(e) => {
                    e.preventDefault();
                    setViewing(t);
                  }}
                >
                  <i className="icon pencil" />
                </button>
              </div>
            )}
          </div>
        ),
      },
    ],
    []
  );

  const headerActions = (
    <div className="float-right" style={{ display: "flex", gap: 8, fontSize: "large" }}>
      <Icon
        name="microphone"
        title="Record new transcript"
        className="btn p-0 m-0"
        onClick={(e) => {
          e.stopPropagation();
          setShowRecord(true);
        }}
      />
      <Icon
        name="cloud upload"
        title="Upload audio"
        className="btn p-0 m-0"
        onClick={(e) => {
          e.stopPropagation();
          setShowUpload(true);
        }}
      />
      <Icon
        name="refresh"
        title="Refresh List"
        className="btn p-0 m-0"
        onClick={(e) => {
          e.stopPropagation();
          fetchTranscripts();
        }}
      />
    </div>
  );

  return (
    <div>

      <Accordion.Title active={shownOpen} onClick={handleTitleClick}>
        <Icon name="dropdown" />
        AI Transcripts
        {shownOpen && headerActions}
      </Accordion.Title>




      <Accordion.Content active={shownOpen}>
        <Table2
          searchable
          hiddenColumns={["appointmentDate"]}
          data={rows}
          columns={columns}
          loading={loading}
        />
      </Accordion.Content>

      {/* dialogs */}
      <VoiceRecorderDialog
        appointmentId={appointmentId}
        open={showRecord}
        onClose={() => setShowRecord(false)}
        patientId={patientId}
        apiBase=""
        onJobStarted={() => {
          setShowRecord(false);
          fetchTranscripts();
          onJobStarted && onJobStarted();
        }}
      />

      <UploadAudioDialog
        appointmentId={appointmentId}
        open={showUpload}
        onClose={() => setShowUpload(false)}
        patientId={patientId}
        onJobStarted={() => {
          setShowUpload(false);
          fetchTranscripts();
          onJobStarted && onJobStarted();
        }}
      />

      <ModalBox
        open={!!viewing}
        onClose={() => {
          setViewing(null);
          fetchTranscripts();
        }}
        title="Transcript"
        size="fullscreen"
      >
        {viewing && <ViewTranscript transcript={viewing} onClose={() => {
          setViewing(null);
          fetchTranscripts();
        }} isModal />}
      </ModalBox>
    </div>
  );
};

export default TranscriptsPanel;
